import { useInView as useFramerInView } from "framer-motion";
import { useEffect, useState } from "react";

type FramerRef = Parameters<typeof useFramerInView>[0];
type FramerOptions = NonNullable<Parameters<typeof useFramerInView>[1]>;

/**
 * Drop-in replacement for framer-motion's useInView for reveal-on-scroll
 * animations.
 *
 * IntersectionObserver-driven reveals proved unreliable on iOS Safari:
 * scroll restoration happens after hydration (so a mount-time position
 * check sees the page still at the top), and observers were seen not
 * firing for elements entering the viewport from the top during upward
 * scrolling - leaving sections above the restored position stuck at
 * opacity 0.
 *
 * So alongside the observer (which still provides the early trigger via
 * its margin), this hook geometrically re-evaluates the element on mount
 * and on every scroll event: as soon as its top edge is at or above the
 * fold it is revealed, whichever direction it arrived from. The listener
 * is passive, rAF-throttled, and detaches itself after the first reveal.
 */
export function useInView(ref: FramerRef, options?: FramerOptions): boolean {
  const inView = useFramerInView(ref, options);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (inView || revealed) return;
    const el = ref.current;
    if (!el) return;

    let raf = 0;
    let done = false;
    const cleanup = () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
    const evaluate = () => {
      if (done) return;
      if (el.getBoundingClientRect().top <= window.innerHeight) {
        done = true;
        cleanup();
        setRevealed(true);
      }
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        evaluate();
      });
    };

    evaluate();
    if (!done) {
      window.addEventListener("scroll", onScroll, { passive: true });
    }
    return cleanup;
  }, [ref, inView, revealed]);

  return inView || revealed;
}
